import { useContext } from 'react';
import { PageContainer } from '../../components/PageContainer';
import { Typography } from '../../components/ui/Typography';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { AuthContext } from '../../contexts/AuthContextDefinition';
import { trpc } from '../../lib/trpc';
import { LeaderboardRow } from './LeaderboardRow';
import { ViewerRankCallout } from './ViewerRankCallout';
import { LeaderboardEmptyState } from './LeaderboardEmptyState';

const SPARSE_THRESHOLD = 10;
const SKELETON_ROWS = 25;

const SkeletonRow = () => (
  <div className="flex items-center gap-4 px-3 md:px-4 py-3 border-b border-gray-200 animate-pulse">
    <div className="flex-shrink-0 w-10 md:w-12 h-6 bg-gray-100 rounded" />
    <div className="w-10 h-10 rounded-full bg-gray-100" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-4 bg-gray-100 rounded w-1/3" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
    </div>
    <div className="w-14 h-10 bg-gray-100 rounded" />
  </div>
);

export const CheckInLeaderboard = () => {
  const auth = useContext(AuthContext);
  const sessionUser = auth?.user ?? null;

  const { data, isLoading, error, refetch, isRefetching } =
    trpc.checkInLeaderboard.top.useQuery(undefined, {
      // Public surface; allow background revalidation when the user returns.
      staleTime: 30 * 1000,
    });

  // Resolve a viewer label for the callout. The viewer's display name on the
  // leaderboard follows the same fullName-privacy rule as the board, but the
  // client doesn't know that flag — so we mirror the server-safe fallback:
  // prefer the user's `name` (only if the server already exposed it via the
  // session), otherwise fall back to `userName`. If neither is present we
  // show "You" as a graceful default so the callout isn't a confusing blank.
  const viewerProfile = sessionUser
    ? {
        userId: sessionUser.id,
        userName: sessionUser.userName ?? sessionUser.email,
        displayName:
          sessionUser.name || sessionUser.userName || 'You',
        avatarUrl: sessionUser.avatar || sessionUser.image || null,
      }
    : null;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer>
        <div className="px-3 md:px-4 pt-2 pb-4">
          <Typography variant="heading1" as="h1" className="text-gray-900">
            Check-in Leader Board
          </Typography>
          <p className="font-sans text-base text-gray-600 mt-2 max-w-2xl">
            The most-traveled wine explorers, ranked by distinct wineries visited.
          </p>
        </div>

        {isLoading && (
          <div>
            {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        )}

        {error && !isLoading && (
          <div className="px-3 md:px-4 py-4">
            <ErrorDisplay
              type="error"
              title="Couldn't load the leader board"
              message={error.message}
              showRetry
              onRetry={() => refetch()}
              isRetrying={isRefetching}
            />
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            {data.totalEligibleUsers === 0 && (
              <LeaderboardEmptyState variant="zero" />
            )}

            {data.totalEligibleUsers > 0 &&
              data.totalEligibleUsers < SPARSE_THRESHOLD && (
                <LeaderboardEmptyState variant="sparse" />
              )}

            {data.rows.length > 0 && (
              <div>
                {data.rows.map((row) => (
                  <LeaderboardRow key={row.userId} row={row} />
                ))}
              </div>
            )}

            {data.viewerRank && viewerProfile && (
              <ViewerRankCallout
                viewerRank={data.viewerRank}
                viewer={viewerProfile}
              />
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
};
